import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as CryptoJS from 'crypto-js';

// =============================================================================
// Client-side encryption for cached evidence (IndexedDB + localStorage).
//
// SECURITY-AUDIT-2026-07.md CR-2 remediation (2026-08-09):
//
//   WRITE PATH (v3): Web Crypto AES-256-GCM.
//     - 96-bit random IV + 128-bit random salt per record (crypto.getRandomValues)
//     - key derived from the caller's key string via PBKDF2-SHA256
//     - GCM's auth tag means any tampering with stored ciphertext fails closed
//       on decrypt (chain-of-custody protection the old CBC path never had)
//     - format: "FN3:" + b64(salt) + ":" + b64(iv) + ":" + b64(ciphertext)
//
//   READ PATH: v3 records decrypt via Web Crypto. Anything WITHOUT the FN3:
//     prefix falls back to the legacy CryptoJS path (EvpKDF/AES-CBC) so
//     previously cached data — including unsynced offline notes that exist
//     nowhere else — remains readable. Callers re-encrypt on their normal
//     write cycles, so caches upgrade to v3 opportunistically.
//
//   KDF NOTE: the key strings fed to this service are machine-generated,
//     high-entropy values (server-issued KEKs, GUID CEKs) — never raw user
//     passwords — so PBKDF2 here is a key-formatting KDF, not password
//     stretching. Iterations are kept modest for read-loop performance over
//     large cached lists. If a human-chosen password is ever fed directly
//     into this service, raise ITERATIONS to 6 figures first.
//
// The API is now async (Web Crypto is Promise-based). "PGP" in the class name
// is historical — nothing OpenPGP about it; kept to avoid churn across the
// injection sites.
// =============================================================================
@Injectable({
  providedIn: 'root'
})
export class PGPEncryptionService {

  private static readonly V3_PREFIX = 'FN3:';
  private static readonly PBKDF2_ITERATIONS = 2048;
  private static readonly SALT_BYTES = 16;
  private static readonly IV_BYTES = 12;

  constructor(
    private router: Router,
  ) {
  }

  // =====================================================================
  // Encrypt — always writes the v3 (AES-GCM) format.
  // =====================================================================
  async encryptData(dataStr: string, contentEncryptionKey: string, optionID: string = ""): Promise<string> {

    // Removed: encryption key must never be logged. SECURITY-AUDIT-2026-07.md (CR-1).

    const bytes = new TextEncoder().encode(dataStr);
    const inputIsValid = new TextDecoder().decode(bytes).length == dataStr.length;
    if (!inputIsValid) {
      console.error("Input is not a valid UTF-8 encoded string");
      return;
    }

    const salt = crypto.getRandomValues(new Uint8Array(PGPEncryptionService.SALT_BYTES));
    const iv = crypto.getRandomValues(new Uint8Array(PGPEncryptionService.IV_BYTES));
    const key = await this.deriveKey(contentEncryptionKey, salt);

    const cipherBuffer = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, bytes);

    return PGPEncryptionService.V3_PREFIX
      + this.toBase64(salt) + ':'
      + this.toBase64(iv) + ':'
      + this.toBase64(new Uint8Array(cipherBuffer));
  }

  // =====================================================================
  // Decrypt — v3 records via Web Crypto (authenticated; wrong key or a
  // tampered record throws and returns undefined), everything else via the
  // legacy CryptoJS path so pre-migration caches stay readable.
  // =====================================================================
  async decryptData(ciphertext: string, contentEncryptionKey: string, optionID: string = ""): Promise<string> {

    if (!ciphertext) {
      return undefined;
    }

    if (ciphertext.startsWith(PGPEncryptionService.V3_PREFIX)) {
      try {
        const parts = ciphertext.substring(PGPEncryptionService.V3_PREFIX.length).split(':');
        if (parts.length !== 3) {
          console.error("INDEX-DB --> decryptData() malformed v3 record --> OptionID = " + optionID);
          return undefined;
        }
        const salt = this.fromBase64(parts[0]);
        const iv = this.fromBase64(parts[1]);
        const cipherBytes = this.fromBase64(parts[2]);

        const key = await this.deriveKey(contentEncryptionKey, salt);
        const plainBuffer = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, cipherBytes);
        return new TextDecoder().decode(plainBuffer);
      }
      catch (exception) {
        // GCM auth failure: wrong key OR the stored record was modified.
        console.error("INDEX-DB --> decryptData() v3 auth failure (wrong key or tampered record) --> OptionID = " + optionID);
        return undefined;
      }
    }

    return this.decryptLegacy(ciphertext, contentEncryptionKey, optionID);
  }

  // =====================================================================
  // Legacy read path (pre-2026-08 records): CryptoJS string-key mode
  // (EvpKDF + AES-CBC, unauthenticated). Kept ONLY so existing caches and
  // unsynced offline notes remain readable; never used for new writes.
  // =====================================================================
  private decryptLegacy(ciphertext: string, contentEncryptionKey: string, optionID: string): string {
    const bytes = CryptoJS.AES.decrypt(ciphertext, contentEncryptionKey);

    // CBC without a MAC produces garbage (not an error) on a wrong key, so
    // the UTF-8 round-trip check stays as the legacy integrity heuristic.
    try {
      let decryptedString: string = bytes.toString(CryptoJS.enc.Utf8);
      let bytes2 = new TextEncoder().encode(decryptedString);
      let inputIsValid = new TextDecoder().decode(bytes2).length == decryptedString.length;
      if (!inputIsValid) {
        console.error("INDEX-DB --> Input is not a valid UTF-8 encoded string")
        return;
      }

      return decryptedString;
    }
    catch (exception) {
      console.error("INDEX-DB --> decryptData() legacy error --> OptionID = " + optionID, exception)
    }
  }

  // =====================================================================
  //
  // =====================================================================
  private async deriveKey(keyString: string, salt: Uint8Array): Promise<CryptoKey> {
    const baseKey = await crypto.subtle.importKey(
      'raw',
      new TextEncoder().encode(keyString),
      'PBKDF2',
      false,
      ['deriveKey']
    );

    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt,
        iterations: PGPEncryptionService.PBKDF2_ITERATIONS,
        hash: 'SHA-256',
      },
      baseKey,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );
  }

  private toBase64(bytes: Uint8Array): string {
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  private fromBase64(b64: string): Uint8Array {
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

}
